import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

// ============================================================
// TYPES
// ============================================================

export interface MeetingEvent {
  id: string
  title: string
  start: string
  end: string
  backgroundColor: string
  borderColor: string
  textColor: string
  extendedProps: {
    meetingId: string
    dealId: string
    dealNumber: number
    dealName: string
    funnelId: string
    stageId: string
    stageName: string
    stageColor: string
    contactId: string | null
    contactName: string | null
    note: string | null
    type: 'STUDIO' | 'ONLINE'
    createdById: string
    createdByName: string | null
    participants: Array<{ id: string; name: string | null }>
    hasParticipantOverlap: boolean
  }
}

export interface MeetingData {
  id: string
  dealId: string
  startDatetime: string
  endDatetime: string
  note: string | null
  type: 'STUDIO' | 'ONLINE'
  createdById: string
  deal: {
    id: string
    number: number
    name: string
    funnelId: string
    stage: { id: string; name: string; color: string | null }
    contact: { id: string; name: string } | null
  }
  createdBy: { id: string; name: string | null }
  participants?: Array<{ id: string; name: string | null }>
  hasParticipantOverlap?: boolean
}

interface MonthCounts {
  [date: string]: number
}

interface MeetingCalendarState {
  events: MeetingEvent[]
  monthCounts: MonthCounts
  loading: boolean
  selectedMeeting: MeetingData | null
  sidebarOpen: boolean
  error: string | null
}

const initialState: MeetingCalendarState = {
  events: [],
  monthCounts: {},
  loading: false,
  selectedMeeting: null,
  sidebarOpen: false,
  error: null
}

// ============================================================
// CONSTANTS
// ============================================================

// UI-SPEC Locked Choice: нейтральный серый fallback для встреч (НЕ '#6366F1' — это Booking).
// Отличается от Booking, чтобы визуально не смешиваться с акцентной палитрой календаря бронирований.
const DEFAULT_MEETING_COLOR = '#6B7280'

// ============================================================
// HELPERS
// ============================================================

/**
 * Blend a hex color with white so it reads well as a background for dark text.
 * Ported from booking-calendar slice — локальная копия per PATTERNS §9 YAGNI
 * (не выделяем в shared, т.к. пока два места; рефакторинг — при появлении третьего клиента).
 */
export function lightenForBackground(color: string, tint = 0.2): string {
  const hex = color.trim()
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex)

  if (!match) return color

  const int = parseInt(match[1], 16)
  const r = (int >> 16) & 0xff
  const g = (int >> 8) & 0xff
  const b = int & 0xff
  const mix = (channel: number) => Math.round(channel * tint + 255 * (1 - tint))
  const toHex = (n: number) => n.toString(16).padStart(2, '0')

  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`
}

export function mapMeetingToEvent(meeting: MeetingData): MeetingEvent {
  const color = meeting.deal.stage.color || DEFAULT_MEETING_COLOR
  const background = lightenForBackground(color)

  return {
    id: meeting.id,
    title: meeting.deal.name,
    start: meeting.startDatetime,
    end: meeting.endDatetime,
    backgroundColor: background,
    borderColor: color,
    textColor: '#1f2937',
    extendedProps: {
      meetingId: meeting.id,
      dealId: meeting.dealId,
      dealNumber: meeting.deal.number,
      dealName: meeting.deal.name,
      funnelId: meeting.deal.funnelId,
      stageId: meeting.deal.stage.id,
      stageName: meeting.deal.stage.name,
      stageColor: color,
      contactId: meeting.deal.contact?.id ?? null,
      contactName: meeting.deal.contact?.name ?? null,
      note: meeting.note,
      type: meeting.type,
      createdById: meeting.createdById,
      createdByName: meeting.createdBy.name,
      participants: meeting.participants ?? [],
      hasParticipantOverlap: meeting.hasParticipantOverlap ?? false
    }
  }
}

// ============================================================
// API ERROR (client-side)
// ============================================================

/**
 * Клиентская обёртка над API-ошибкой, сохраняющая опциональный `code`
 * (см. api-middleware.ts ApiError). Используется thunks для передачи
 * discriminator'а в UI — избегаем string-match'а по message.
 */
export class ApiClientError extends Error {
  public readonly code?: string
  public readonly status?: number

  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message)
    this.name = 'ApiClientError'
    this.code = opts?.code
    this.status = opts?.status
  }
}

// ============================================================
// THUNKS
// ============================================================

export const fetchMeetings = createAsyncThunk(
  'meetingCalendar/fetchMeetings',
  async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    const params = new URLSearchParams({ startDate, endDate })
    const res = await fetch(`/api/meetings?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Не удалось загрузить встречи')
    }

    return (json.data as MeetingData[]).map(mapMeetingToEvent)
  }
)

export const fetchMonthCounts = createAsyncThunk(
  'meetingCalendar/fetchMonthCounts',
  async ({ year, month }: { year: number; month: number }) => {
    const params = new URLSearchParams({
      countByDay: 'true',
      year: String(year),
      month: String(month)
    })

    const res = await fetch(`/api/meetings?${params}`)
    const json = await res.json()

    if (!json.success) {
      throw new Error(json.error ?? 'Не удалось загрузить сводку по дням')
    }

    // Meeting API возвращает Record<'yyyy-MM-dd', number> напрямую
    // (см. /api/meetings/route.ts mode countByDay).
    return json.data as MonthCounts
  }
)

export const createMeeting = createAsyncThunk(
  'meetingCalendar/createMeeting',
  async (data: {
    dealId: string
    startDateTime: string
    endDateTime: string
    note?: string
    participantIds?: string[]
    type?: 'STUDIO' | 'ONLINE'
  }) => {
    const res = await fetch('/api/meetings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка создания встречи', {
        code: json.code,
        status: res.status
      })
    }

    return mapMeetingToEvent(json.data as MeetingData)
  }
)

export const updateMeeting = createAsyncThunk(
  'meetingCalendar/updateMeeting',
  async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
    const res = await fetch(`/api/meetings/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка обновления встречи', {
        code: json.code,
        status: res.status
      })
    }

    return mapMeetingToEvent(json.data as MeetingData)
  }
)

export const deleteMeeting = createAsyncThunk(
  'meetingCalendar/deleteMeeting',
  async (id: string) => {
    const res = await fetch(`/api/meetings/${id}`, {
      method: 'DELETE'
    })

    const json = await res.json()

    if (!json.success) {
      throw new ApiClientError(json.error ?? 'Ошибка удаления встречи', {
        code: json.code,
        status: res.status
      })
    }

    return id
  }
)

// ============================================================
// SLICE
// ============================================================

export const meetingCalendarSlice = createSlice({
  name: 'meetingCalendar',
  initialState,
  reducers: {
    setSelectedMeeting: (state, action: PayloadAction<MeetingData | null>) => {
      return { ...state, selectedMeeting: action.payload }
    },
    openSidebar: (state) => {
      return { ...state, sidebarOpen: true }
    },
    closeSidebar: (state) => {
      return { ...state, sidebarOpen: false, selectedMeeting: null }
    },
    clearMonthCounts: (state) => {
      return { ...state, monthCounts: {} }
    }
  },
  extraReducers: (builder) => {
    // fetchMeetings
    builder.addCase(fetchMeetings.pending, (state) => {
      return { ...state, loading: true, error: null }
    })

    builder.addCase(fetchMeetings.fulfilled, (state, action) => {
      return { ...state, events: action.payload, loading: false }
    })

    builder.addCase(fetchMeetings.rejected, (state, action) => {
      return { ...state, loading: false, error: action.error.message ?? 'Не удалось загрузить встречи' }
    })

    // fetchMonthCounts
    builder.addCase(fetchMonthCounts.fulfilled, (state, action) => {
      return { ...state, monthCounts: action.payload }
    })

    builder.addCase(fetchMonthCounts.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Не удалось загрузить сводку по дням' }
    })

    // createMeeting
    builder.addCase(createMeeting.fulfilled, (state, action) => {
      return {
        ...state,
        events: [...state.events, action.payload],
        sidebarOpen: false,
        selectedMeeting: null
      }
    })

    builder.addCase(createMeeting.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка создания встречи' }
    })

    // updateMeeting
    builder.addCase(updateMeeting.fulfilled, (state, action) => {
      const updatedEvent = action.payload
      const updatedEvents = state.events.map((evt) => (evt.id === updatedEvent.id ? updatedEvent : evt))

      return { ...state, events: updatedEvents, sidebarOpen: false, selectedMeeting: null }
    })

    builder.addCase(updateMeeting.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка обновления встречи' }
    })

    // deleteMeeting
    builder.addCase(deleteMeeting.fulfilled, (state, action) => {
      const deletedId = action.payload
      const filteredEvents = state.events.filter((evt) => evt.id !== deletedId)

      return { ...state, events: filteredEvents, sidebarOpen: false, selectedMeeting: null }
    })

    builder.addCase(deleteMeeting.rejected, (state, action) => {
      return { ...state, error: action.error.message ?? 'Ошибка удаления встречи' }
    })
  }
})

export const { setSelectedMeeting, openSidebar, closeSidebar, clearMonthCounts } = meetingCalendarSlice.actions

export default meetingCalendarSlice.reducer
